import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { updateCompany } from '../../api/companies';
import { reportFeedIssue, resolveFeedIssuesForCompany } from '../../api/contentCompletion';
import {
  CompanyContentHealth,
  UrlCompanyFields,
  UrlDiscoveryResult,
} from '../../types/contentCompletion';
import { CompanyUpdate } from '../../types/company';
import { useNotification } from '../../context/NotificationContext';
import { useEnvironment } from '../../hooks/useEnvironment';
import {
  DISPLAY_URL_FIELDS,
  URL_FIELD_LABELS,
  UrlDraftFields,
  buildNeedsReviewReportRequest,
  buildRejectedUrlDiscoveryReportRequest,
  buildUrlDraftFromDiscovery,
  buildUrlUpdatePayloadFromDraft,
  discoveryMissingPressSources,
  draftHasUrlChanges,
  formatUrlFieldValue,
  getChangedUrlFields,
} from './urlDiscoveryUtils';

export interface BatchUrlDiscoveryReviewItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  discoveryResult: UrlDiscoveryResult;
}

interface BatchUrlDiscoveryReviewDialogProps {
  open: boolean;
  onClose: () => void;
  items: BatchUrlDiscoveryReviewItem[];
  onCompanySaved?: (companyId: string) => void;
  onComplete?: () => void;
  onIssueResolved?: () => void;
  readOnly?: boolean;
  title?: string;
}

const UrlCell: React.FC<{ value?: string | null }> = ({ value }) => {
  if (!value) return <>{formatUrlFieldValue(value)}</>;
  return (
    <Link href={value} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
      {value}
      <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
    </Link>
  );
};

const EditableUrlCell: React.FC<{
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onReset?: () => void;
  resetDisabled?: boolean;
}> = ({ value, disabled, onChange, onReset, resetDisabled }) => {
  const openHref = value.trim()
    ? /^https?:\/\//i.test(value.trim())
      ? value.trim()
      : `https://${value.trim()}`
    : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 280 }}>
      <TextField
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        placeholder="https://…"
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Edit URL' }}
        sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
      />
      {openHref && (
        <Tooltip title="Open URL">
          <IconButton
            size="small"
            component="a"
            href={openHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onReset && (
        <Tooltip title="Reset to discovery proposal">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              disabled={disabled || resetDisabled}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

const BatchUrlDiscoveryReviewDialog: React.FC<BatchUrlDiscoveryReviewDialogProps> = ({
  open,
  onClose,
  items,
  onCompanySaved,
  onComplete,
  onIssueResolved,
  readOnly = false,
  title,
}) => {
  const { showNotification } = useNotification();
  const { environment } = useEnvironment();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
  const [savedCompanyIds, setSavedCompanyIds] = useState<Set<string>>(new Set());
  const [reportedCompanyIds, setReportedCompanyIds] = useState<Set<string>>(new Set());
  const [expandedCompanyId, setExpandedCompanyId] = useState<string | false>(false);
  const [drafts, setDrafts] = useState<Record<string, UrlDraftFields>>({});
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [saveProgress, setSaveProgress] = useState<string | null>(null);
  const [reportProgress, setReportProgress] = useState<string | null>(null);
  const [confirmProductionOpen, setConfirmProductionOpen] = useState(false);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  const [pendingSaveAll, setPendingSaveAll] = useState(false);
  const [singleSaveItem, setSingleSaveItem] = useState<BatchUrlDiscoveryReviewItem | null>(null);
  const wasOpenRef = useRef(false);

  const getDraft = useCallback(
    (item: BatchUrlDiscoveryReviewItem): UrlDraftFields =>
      drafts[item.companyId] ?? buildUrlDraftFromDiscovery(item.discoveryResult),
    [drafts]
  );

  const itemHasEditableChanges = useCallback(
    (item: BatchUrlDiscoveryReviewItem): boolean =>
      draftHasUrlChanges(item.discoveryResult.current, getDraft(item)),
    [getDraft]
  );

  // Seed drafts on open; when items grow mid-batch, only add missing companies —
  // never overwrite in-progress edits.
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    const opening = !wasOpenRef.current;
    wasOpenRef.current = true;

    const seededById: Record<string, UrlDraftFields> = {};
    for (const item of items) {
      seededById[item.companyId] = buildUrlDraftFromDiscovery(item.discoveryResult);
    }

    if (opening) {
      setDrafts(seededById);
      const initial = new Set(
        items
          .filter(
            (item) =>
              item.discoveryResult.auto_select &&
              draftHasUrlChanges(item.discoveryResult.current, seededById[item.companyId])
          )
          .map((item) => item.companyId)
      );
      setSelectedCompanyIds(initial);
      setSavedCompanyIds(new Set());
      setReportedCompanyIds(new Set());
      setExpandedCompanyId(items[0]?.companyId ?? false);
      setSaveProgress(null);
      setReportProgress(null);
      setConfirmProductionOpen(false);
      setProductionConfirmed(false);
      setPendingSaveAll(false);
      setSingleSaveItem(null);
      return;
    }

    setDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        if (!next[item.companyId]) {
          next[item.companyId] = seededById[item.companyId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setSelectedCompanyIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const item of items) {
        if (
          !next.has(item.companyId) &&
          item.discoveryResult.auto_select &&
          draftHasUrlChanges(item.discoveryResult.current, seededById[item.companyId])
        ) {
          next.add(item.companyId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, items]);

  const pendingItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !savedCompanyIds.has(item.companyId) &&
          !reportedCompanyIds.has(item.companyId) &&
          itemHasEditableChanges(item)
      ),
    [items, savedCompanyIds, reportedCompanyIds, itemHasEditableChanges]
  );

  const selectedPendingCount = useMemo(
    () => pendingItems.filter((item) => selectedCompanyIds.has(item.companyId)).length,
    [pendingItems, selectedCompanyIds]
  );

  const rejectedPendingCount = useMemo(
    () => pendingItems.filter((item) => !selectedCompanyIds.has(item.companyId)).length,
    [pendingItems, selectedCompanyIds]
  );

  const isBusy = saving || reporting;

  const updateDraftField = useCallback(
    (companyId: string, field: keyof UrlCompanyFields, value: string) => {
      setDrafts((prev) => {
        const existing =
          prev[companyId] ??
          buildUrlDraftFromDiscovery(
            items.find((i) => i.companyId === companyId)?.discoveryResult || {
              proposed: {},
              current: {},
            } as UrlDiscoveryResult
          );
        return {
          ...prev,
          [companyId]: { ...existing, [field]: value },
        };
      });
      // Selecting after a manual edit makes the company eligible for save-all.
      setSelectedCompanyIds((prev) => {
        if (prev.has(companyId)) return prev;
        const next = new Set(prev);
        next.add(companyId);
        return next;
      });
    },
    [items]
  );

  const resetDraftField = useCallback(
    (item: BatchUrlDiscoveryReviewItem, field: keyof UrlCompanyFields) => {
      const seeded = buildUrlDraftFromDiscovery(item.discoveryResult);
      updateDraftField(item.companyId, field, seeded[field]);
    },
    [updateDraftField]
  );

  const toggleCompany = useCallback((companyId: string) => {
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  }, []);

  const markSaved = (companyId: string) => {
    setSavedCompanyIds((prev) => new Set(prev).add(companyId));
    onCompanySaved?.(companyId);
    onComplete?.();
  };

  const markReported = (companyId: string) => {
    setReportedCompanyIds((prev) => new Set(prev).add(companyId));
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      next.delete(companyId);
      return next;
    });
    onIssueResolved?.();
  };

  const saveCompany = async (item: BatchUrlDiscoveryReviewItem) => {
    const draft = getDraft(item);
    const fieldPayload = buildUrlUpdatePayloadFromDraft(item.discoveryResult.current, draft);
    if (Object.keys(fieldPayload).length === 0) {
      throw new Error('No changes to save');
    }
    const payload = fieldPayload as CompanyUpdate;
    await updateCompany(item.companyId, payload);
    try {
      await resolveFeedIssuesForCompany(item.companyId);
      onIssueResolved?.();
    } catch {
      /* save succeeded even if issue resolve fails */
    }
  };

  const reportMismatch = async (item: BatchUrlDiscoveryReviewItem) => {
    await reportFeedIssue(
      buildRejectedUrlDiscoveryReportRequest(
        item.companyId,
        item.companyName,
        item.discoveryResult
      )
    );
  };

  const reportForReview = async (item: BatchUrlDiscoveryReviewItem) => {
    await reportFeedIssue(
      buildNeedsReviewReportRequest(
        item.companyId,
        item.companyName,
        item.discoveryResult,
        getDraft(item)
      )
    );
  };

  const handleReportMismatch = async (item: BatchUrlDiscoveryReviewItem) => {
    setReporting(true);
    setReportProgress(item.companyName);
    try {
      await reportMismatch(item);
      showNotification(`${item.companyName}: reported as rejected match`, 'success');
      markReported(item.companyId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to report ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setReporting(false);
      setReportProgress(null);
    }
  };

  const handleReportCompany = async (item: BatchUrlDiscoveryReviewItem) => {
    setReporting(true);
    setReportProgress(item.companyName);
    try {
      await reportForReview(item);
      showNotification(`${item.companyName}: added to Companies to be Reviewed`, 'success');
      markReported(item.companyId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to report ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setReporting(false);
      setReportProgress(null);
    }
  };

  const runReportMismatches = async () => {
    const toReport = pendingItems.filter((item) => !selectedCompanyIds.has(item.companyId));
    if (toReport.length === 0) {
      showNotification('No unchecked companies to report', 'warning');
      return;
    }

    setReporting(true);
    let reported = 0;
    let failed = 0;

    for (let i = 0; i < toReport.length; i += 1) {
      const item = toReport[i];
      setReportProgress(`${item.companyName} (${i + 1}/${toReport.length})`);
      try {
        await reportMismatch(item);
        markReported(item.companyId);
        reported += 1;
      } catch (err: unknown) {
        failed += 1;
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to report ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }

    setReporting(false);
    setReportProgress(null);
    if (reported > 0) {
      showNotification(
        `Reported ${reported} mismatch${reported === 1 ? '' : 'es'} to URL / feed issues`,
        'success'
      );
    }
    if (failed > 0 && reported === 0) {
      showNotification('No mismatches were reported', 'error');
    }
  };

  const handleReportMismatches = () => {
    if (rejectedPendingCount === 0) {
      showNotification('No unchecked companies to report', 'warning');
      return;
    }
    runReportMismatches();
  };

  const runReportAllForReview = async () => {
    const eligible = items.filter(
      (item) =>
        !savedCompanyIds.has(item.companyId) && !reportedCompanyIds.has(item.companyId)
    );
    if (eligible.length === 0) {
      showNotification('No companies left to report', 'warning');
      return;
    }

    setReporting(true);
    let reported = 0;
    for (let i = 0; i < eligible.length; i += 1) {
      const item = eligible[i];
      setReportProgress(`${item.companyName} (${i + 1}/${eligible.length})`);
      try {
        await reportForReview(item);
        markReported(item.companyId);
        reported += 1;
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to report ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }
    setReporting(false);
    setReportProgress(null);
    if (reported > 0) {
      showNotification(
        `Added ${reported} compan${reported === 1 ? 'y' : 'ies'} to Companies to be Reviewed`,
        'success'
      );
    }
  };

  const reviewEligibleCount = useMemo(
    () =>
      items.filter(
        (item) =>
          !savedCompanyIds.has(item.companyId) && !reportedCompanyIds.has(item.companyId)
      ).length,
    [items, savedCompanyIds, reportedCompanyIds]
  );

  const handleSaveCompany = async (item: BatchUrlDiscoveryReviewItem) => {
    setSaving(true);
    setSaveProgress(item.companyName);
    try {
      await saveCompany(item);
      showNotification(`${item.companyName}: URLs saved`, 'success');
      markSaved(item.companyId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        `Failed to save ${item.companyName}`;
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
      setSaveProgress(null);
    }
  };

  const runSaveAll = async () => {
    const toSave = pendingItems.filter((item) => selectedCompanyIds.has(item.companyId));
    if (toSave.length === 0) {
      showNotification('No companies selected to save', 'warning');
      return;
    }

    setSaving(true);
    let saved = 0;
    let failed = 0;

    for (let i = 0; i < toSave.length; i += 1) {
      const item = toSave[i];
      setSaveProgress(`${item.companyName} (${i + 1}/${toSave.length})`);
      try {
        await saveCompany(item);
        markSaved(item.companyId);
        saved += 1;
      } catch (err: unknown) {
        failed += 1;
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          `Failed to save ${item.companyName}`;
        showNotification(msg, 'error');
      }
    }

    setSaving(false);
    setSaveProgress(null);
    if (saved > 0) {
      showNotification(`Saved URLs for ${saved} compan${saved === 1 ? 'y' : 'ies'}`, 'success');
    }
    if (failed > 0 && saved === 0) {
      showNotification('No companies were saved', 'error');
    }
  };

  const handleSaveCompanyWithProductionCheck = (item: BatchUrlDiscoveryReviewItem) => {
    if (!itemHasEditableChanges(item)) {
      showNotification('No URL changes to save for this company', 'warning');
      return;
    }
    if (environment === 'production') {
      setSingleSaveItem(item);
      setPendingSaveAll(false);
      setProductionConfirmed(false);
      setConfirmProductionOpen(true);
      return;
    }
    handleSaveCompany(item);
  };

  const handleSaveAll = () => {
    if (selectedPendingCount === 0) {
      showNotification('No companies selected to save', 'warning');
      return;
    }
    if (environment === 'production') {
      setPendingSaveAll(true);
      setSingleSaveItem(null);
      setProductionConfirmed(false);
      setConfirmProductionOpen(true);
      return;
    }
    runSaveAll();
  };

  const handleConfirmProduction = async () => {
    setConfirmProductionOpen(false);
    if (pendingSaveAll) {
      setPendingSaveAll(false);
      await runSaveAll();
    } else if (singleSaveItem) {
      const item = singleSaveItem;
      setSingleSaveItem(null);
      await handleSaveCompany(item);
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const dialogTitle =
    title ||
    (readOnly
      ? `URL validation — ${items.length} compan${items.length === 1 ? 'y' : 'ies'}`
      : `Review URL discovery — ${items.length} compan${items.length === 1 ? 'y' : 'ies'}`);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {environment === 'production' && !readOnly && (
              <Alert severity="warning">
                You are in <strong>production</strong> mode. Saving will write to the live Neo4j
                database.
              </Alert>
            )}

            {readOnly ? (
              <Typography variant="body2" color="text.secondary">
                Validation details. Use <strong>Report Company</strong> to add incomplete or
                uncertain cases to Companies to be Reviewed (no Neo4j write).
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Edit proposed URLs before saving (paste known-good links as needed).{' '}
                {selectedPendingCount} of {pendingItems.length} pending companies selected for save.
                Use <strong>Report Company</strong> for cases that need follow-up (e.g. no PR/RSS).
              </Typography>
            )}

            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Saving to Neo4j{saveProgress ? ` — ${saveProgress}` : '…'}
                </Typography>
              </Box>
            )}

            {reporting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Reporting mismatches{reportProgress ? ` — ${reportProgress}` : '…'}
                </Typography>
              </Box>
            )}

            {items.map((item) => {
              const isSaved = savedCompanyIds.has(item.companyId);
              const isReported = reportedCompanyIds.has(item.companyId);
              const isSelected = selectedCompanyIds.has(item.companyId);
              const { discoveryResult } = item;
              const draft = getDraft(item);
              const hasChanges = itemHasEditableChanges(item);
              const changedFields = new Set(getChangedUrlFields(discoveryResult.current, draft));
              const canEdit = !readOnly && !isSaved && !isReported;

              return (
                <Accordion
                  key={item.companyId}
                  expanded={expandedCompanyId === item.companyId}
                  onChange={(_, expanded) =>
                    setExpandedCompanyId(expanded ? item.companyId : false)
                  }
                  disabled={isBusy}
                  sx={{ opacity: isSaved || isReported ? 0.7 : 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                        pr: 1,
                      }}
                    >
                      {canEdit && (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleCompany(item.companyId)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isBusy || !hasChanges}
                        />
                      )}
                      <Typography fontWeight={600}>{item.companyName}</Typography>
                      <Chip
                        label={discoveryResult.check_status.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                      />
                      {(() => {
                        const seeded = buildUrlDraftFromDiscovery(discoveryResult);
                        const manuallyEdited = DISPLAY_URL_FIELDS.some(
                          (field) => draft[field] !== seeded[field]
                        );
                        return manuallyEdited && !isSaved && !isReported ? (
                          <Chip label="Edited" size="small" color="info" variant="outlined" />
                        ) : null;
                      })()}
                      {discoveryMissingPressSources(discoveryResult, draft) &&
                        !isSaved &&
                        !isReported && (
                          <Chip
                            label="No PR / RSS"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      {isSaved && <Chip label="Saved" color="success" size="small" />}
                      {isReported && <Chip label="Reported" color="warning" size="small" />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {discoveryResult.summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {discoveryResult.summary}
                      </Typography>
                    )}

                    {discoveryResult.warnings.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {discoveryResult.warnings.map((warning) => (
                          <Typography key={warning} variant="body2">
                            {warning}
                          </Typography>
                        ))}
                      </Alert>
                    )}

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 160 }}>Field</TableCell>
                          <TableCell sx={{ width: '30%' }}>Current</TableCell>
                          <TableCell>{canEdit ? 'Proposed / edit' : 'Value'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {DISPLAY_URL_FIELDS.map((field) => {
                          const currentValue = discoveryResult.current[field];
                          const seeded = buildUrlDraftFromDiscovery(discoveryResult)[field];
                          const draftValue = draft[field];
                          const fieldChanged = changedFields.has(field);
                          return (
                            <TableRow
                              key={field}
                              sx={
                                fieldChanged
                                  ? { backgroundColor: 'action.hover' }
                                  : undefined
                              }
                            >
                              <TableCell>{URL_FIELD_LABELS[field]}</TableCell>
                              <TableCell>
                                <UrlCell value={currentValue} />
                              </TableCell>
                              <TableCell>
                                {canEdit ? (
                                  <EditableUrlCell
                                    value={draftValue}
                                    disabled={isBusy}
                                    onChange={(value) =>
                                      updateDraftField(item.companyId, field, value)
                                    }
                                    onReset={() => resetDraftField(item, field)}
                                    resetDisabled={draftValue === seeded}
                                  />
                                ) : (
                                  <strong>
                                    <UrlCell value={draftValue || currentValue} />
                                  </strong>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {!isSaved && !isReported && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          disabled={isBusy}
                          onClick={() => handleReportCompany(item)}
                        >
                          Report Company
                        </Button>
                        {canEdit && !isSelected && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            disabled={isBusy}
                            onClick={() => handleReportMismatch(item)}
                          >
                            Report mismatch
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={isBusy || !hasChanges || !isSelected}
                            onClick={() => handleSaveCompanyWithProductionCheck(item)}
                          >
                            Save this company
                          </Button>
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleClose} disabled={isBusy}>
            {readOnly || pendingItems.length === 0 ? 'Done' : 'Close'}
          </Button>
          <Box sx={{ flex: 1 }} />
          {readOnly ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={runReportAllForReview}
              disabled={isBusy || reviewEligibleCount === 0}
            >
              {reporting
                ? 'Reporting…'
                : `Report all for review (${reviewEligibleCount})`}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleReportMismatches}
                disabled={isBusy || rejectedPendingCount === 0 || pendingItems.length === 0}
              >
                {reporting
                  ? 'Reporting…'
                  : `Report mismatches (${rejectedPendingCount} companies)`}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveAll}
                disabled={isBusy || selectedPendingCount === 0 || pendingItems.length === 0}
              >
                {saving
                  ? 'Saving…'
                  : `Save all selected (${selectedPendingCount} companies)`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmProductionOpen}
        onClose={() => {
          setConfirmProductionOpen(false);
          setPendingSaveAll(false);
          setSingleSaveItem(null);
        }}
      >
        <DialogTitle>Confirm production save</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingSaveAll ? (
              <>
                You are about to update URLs for{' '}
                <strong>{selectedPendingCount} companies</strong> in the{' '}
                <strong>production</strong> Neo4j database.
              </>
            ) : (
              <>
                You are about to update URLs for <strong>{singleSaveItem?.companyName}</strong> in
                the <strong>production</strong> Neo4j database.
              </>
            )}
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={productionConfirmed}
                onChange={(e) => setProductionConfirmed(e.target.checked)}
              />
            }
            label="I understand this will modify production data"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmProductionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmProduction}
            disabled={!productionConfirmed}
          >
            Save to production
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BatchUrlDiscoveryReviewDialog;
